/* eslint-disable complexity */
import { dFix } from '@/data/Settings';
import { formatPrice } from '@/utils/formatPrice';
import { Box, Divider, Stack, Typography } from '@mui/material';
import {
	OrderSummaryBreakdownDividerSx,
	OrderSummaryBreakdownFrameSx,
	OrderSummaryBreakdownTitleSx,
	OrderSummaryBreakdownTotalSx,
	defaultTypographySx,
} from '@/components/content/Cart/parts/OrderSummaryBreakdown/styles/main';
import { combineSX } from '@/utils/combineSX';

interface OrderSummaryBreakdownProps {
	total: string | number;
	subtotal: string | number;
	shipping: string | number;
	discount: string | number;
	coupons: string | number;
	totalProducts?: number;
	points?: string | number;
	valeDeCaja?: string | number;
	showTitle?: boolean;
	loading?: boolean;
	confirmation?: boolean;
	isCashPayment?: boolean;
	isMiniCart?: boolean;
}

export const OrderSummaryBreakdown: React.FC<OrderSummaryBreakdownProps> = ({
	total,
	subtotal,
	shipping,
	discount,
	coupons,
	points,
	totalProducts = 0,
	valeDeCaja,
	showTitle = true,
	loading = false,
	confirmation = false,
	isCashPayment = false,
	isMiniCart = false,
}) => {
	const isFreeShipping = dFix(shipping || '9', 0) === 0;

	const baseTypographySx = combineSX([
		defaultTypographySx,
		isMiniCart ? { fontSize: 'var(--font-size-default)' } : {},
	]);

	return (
		<Box sx={OrderSummaryBreakdownFrameSx}>
			{isMiniCart ? null : (
				<>
					{showTitle ? (
						<Typography component={'h2'} sx={OrderSummaryBreakdownTitleSx}>
							{'RESUMEN DE TU PEDIDO'}
						</Typography>
					) : null}
					{totalProducts > 0 ? (
						<Stack direction="row" justifyContent="space-between" sx={{ marginBottom: 2 }}>
							<Typography sx={baseTypographySx}>{`${totalProducts} producto${
								totalProducts > 1 ? 's' : ''
							}`}</Typography>
						</Stack>
					) : null}
					<Stack direction="row" justifyContent="space-between">
						<Typography sx={baseTypographySx}>{'Subtotal:'}</Typography>
						<Typography sx={baseTypographySx}>
							{loading ? '...' : formatPrice('es-MX', 'MXN', subtotal)}
						</Typography>
					</Stack>
					<Stack direction="row" justifyContent="space-between">
						<Typography sx={baseTypographySx}>{'Estás ahorrando:'}</Typography>
						<Typography sx={baseTypographySx} color={'#BF0000'}>
							{formatPrice('es-MX', 'MXN', discount)}
						</Typography>
					</Stack>
					{parseFloat(`${coupons}`) > 0 ? (
						<Stack direction="row" justifyContent="space-between">
							<Typography sx={baseTypographySx}>{'Cupones:'}</Typography>
							<Typography sx={baseTypographySx} color={'#BF0000'}>
								{formatPrice('es-MX', 'MXN', coupons)}
							</Typography>
						</Stack>
					) : null}

					{valeDeCaja ? (
						<Stack direction="row" justifyContent="space-between">
							<Typography sx={baseTypographySx}>{'Monedero:'}</Typography>
							<Typography sx={baseTypographySx} color={'#BF0000'}>
								{'-' + formatPrice('es-MX', 'MXN', valeDeCaja)}
							</Typography>
						</Stack>
					) : null}

					{points ? (
						<Stack direction="row" justifyContent="space-between">
							<Typography sx={combineSX([baseTypographySx, { color: '#004A9B', fontWeight: 600 }])}>
								{'Puntos canjeados:'}
							</Typography>
							<Typography sx={combineSX([baseTypographySx, { color: '#004A9B', fontWeight: 600 }])}>
								{'-' + formatPrice('es-MX', 'MXN', points ?? '0')}
							</Typography>
						</Stack>
					) : null}

					<Stack direction="row" justifyContent="space-between">
						<Typography sx={baseTypographySx}>{'Costo de envío:'}</Typography>
						<Typography sx={baseTypographySx} color={isFreeShipping ? '#0E8439' : 'inherit'}>
							{isFreeShipping ? '¡Gratis!' : formatPrice('es-MX', 'MXN', shipping)}
						</Typography>
					</Stack>

					<Divider sx={OrderSummaryBreakdownDividerSx} />
				</>
			)}

			<Stack direction="row" justifyContent="space-between" alignItems={'center'}>
				<Typography
					sx={combineSX([
						{ fontSize: '15px', fontWeight: 800, textTransform: 'uppercase' },
						isMiniCart
							? {
									fontSize: 'var(--font-size-default)',
									fontWeight: 500,
									textTransform: 'capitalize',
							  }
							: {},
					])}
				>
					{confirmation && isCashPayment ? 'Total a pagar:' : 'Total:'}
				</Typography>
				<Typography
					sx={combineSX([
						OrderSummaryBreakdownTotalSx,
						confirmation && isCashPayment ? { color: '#399300', fontSize: '18px' } : {},
						isMiniCart ? { fontSize: 'var(--font-size-default)' } : {},
					])}
				>
					{formatPrice('es-MX', 'MXN', total)}
				</Typography>
			</Stack>
		</Box>
	);
};
